import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { MY_TASKS_KEY, taskStatusSchema } from '@witty-board/shared'
import { useMeQuery } from '../api/auth.js'
import { labelsQueryOptions } from '../api/labels.js'
import { useProjectsQuery } from '../api/projects.js'
import { useTasksQuery, type TaskListFilters } from '../api/tasks.js'
import { useUsersQuery } from '../api/users.js'
import { ProjectSwatch } from '../components/ProjectSwatch.js'
import { DoneWindowSelect } from '../features/board/DoneWindowSelect.js'
import { ColumnTaskItem } from '../features/column/ColumnTaskItem.js'
import { groupTasksByStatus } from '../lib/board.js'
import { boardViewPath, taskPanelPath } from '../lib/board-routes.js'
import { useDoneWindow } from '../lib/done-window.js'
import { sumSizePoints } from '../lib/size.js'

/**
 * Vista de columna (CLAUDE.md §6, fase 7): una sola columna de un proyecto o
 * de Mis tareas, con cada tarea desplegada para revisarla sin abrirla. Pide
 * las tareas con la misma forma de filtro que el tablero de origen, así que
 * comparte su caché y entrar es instantáneo; el panel de tarea se abre encima
 * igual que en el tablero.
 */
export function ColumnPage() {
  const { key = '', status: rawStatus } = useParams<{ key: string; status: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const me = useMeQuery()
  const projects = useProjectsQuery()
  const users = useUsersQuery()
  const { doneSince } = useDoneWindow()

  const parsedStatus = taskStatusSchema.safeParse(rawStatus)
  const status = parsedStatus.success ? parsedStatus.data : null
  const isHome = key === MY_TASKS_KEY
  const project = isHome ? undefined : projects.data?.find((candidate) => candidate.key === key)

  const filters: TaskListFilters = isHome
    ? { assigneeId: me.data?.id, doneSince }
    : { projectId: project?.id, doneSince }
  const tasksQuery = useTasksQuery(filters, {
    enabled: status !== null && (isHome ? me.data !== undefined : project !== undefined),
  })

  const columnTasks = useMemo(
    () => (status ? groupTasksByStatus(tasksQuery.data ?? [])[status] : []),
    [tasksQuery.data, status],
  )
  const projectIds = useMemo(
    () => [...new Set(columnTasks.map((task) => task.projectId))],
    [columnTasks],
  )
  // Una consulta por proyecto presente: en Mis tareas la columna mezcla proyectos.
  const labelQueries = useQueries({
    queries: projectIds.map((projectId) => labelsQueryOptions(projectId)),
  })

  const projectsById = new Map((projects.data ?? []).map((item) => [item.id, item] as const))
  const usersById = new Map((users.data ?? []).map((user) => [user.id, user] as const))
  const labelsById = new Map(
    labelQueries.flatMap((query) => query.data ?? []).map((label) => [label.id, label] as const),
  )

  if (!status || (!isHome && projects.data && !project)) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-24 text-center">
        <p className="text-body text-text">{t('board.notFound.title')}</p>
        <Link to="/" className="text-body text-text underline hover:text-text-muted">
          {t('board.notFound.back')}
        </Link>
      </div>
    )
  }

  const boardName = isHome ? t('home.myTasks') : (project?.name ?? '')

  return (
    <div className="flex flex-col">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-6 pt-6">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to={{ pathname: boardViewPath(key), search: location.search }}
            aria-label={t('column.back')}
            title={t('column.back')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent max-md:h-11 max-md:w-11"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          {project && <ProjectSwatch color={project.color} />}
          <h1 className="truncate text-title font-semibold text-text">
            {t('column.title', { board: boardName, column: t(`board.columns.${status}`) })}
          </h1>
          <span className="shrink-0 text-meta text-text-muted">
            {t('board.column.summary', {
              count: columnTasks.length,
              points: sumSizePoints(columnTasks),
            })}
          </span>
        </div>
        {status === 'done' && <DoneWindowSelect />}
      </header>

      <div className="p-4 md:p-6">
        {tasksQuery.isPending ? (
          <div className="mx-auto flex max-w-4xl flex-col gap-2" aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-40 animate-pulse rounded-card bg-surface" />
            ))}
          </div>
        ) : columnTasks.length === 0 ? (
          <p className="mx-auto max-w-4xl text-body text-text-muted">{t('column.empty')}</p>
        ) : (
          <ul className="mx-auto flex max-w-4xl flex-col gap-2">
            {columnTasks.map((task) => (
              <ColumnTaskItem
                key={task.id}
                task={task}
                project={projectsById.get(task.projectId)}
                assignee={task.assigneeId ? usersById.get(task.assigneeId) : undefined}
                usersById={usersById}
                labelsById={labelsById}
                showProjectKey={isHome}
                onOpen={() =>
                  void navigate({
                    pathname: taskPanelPath(key, task.id, status),
                    search: location.search,
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>

      <Outlet />
    </div>
  )
}
